package com.patronat.top.client.presentation.map

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.client.location.ClientGeoLocation
import com.patronat.top.client.location.ClientMapTilerConfiguration
import com.patronat.top.client.presentation.home.components.ratingSummaryText
import com.patronat.top.client.presentation.home.model.icon
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.DayOfWeek as VenueDayOfWeek
import com.patronat.top.ui.atoms.AppNetworkImage
import com.patronat.top.ui.atoms.AppSearchField
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft400
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.resources.stringResource
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.compose.camera.CameraState
import org.maplibre.compose.camera.rememberCameraState
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.layers.CircleLayer
import org.maplibre.compose.map.MaplibreMap
import org.maplibre.compose.sources.GeoJsonData
import org.maplibre.compose.sources.GeoJsonOptions
import org.maplibre.compose.sources.rememberGeoJsonSource
import org.maplibre.compose.style.BaseStyle
import org.maplibre.compose.util.ClickResult
import org.maplibre.compose.util.MaplibreComposable
import org.maplibre.spatialk.geojson.Position
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_map_empty_title
import top.clientapp.generated.resources.client_map_loading
import top.clientapp.generated.resources.client_map_results_count
import top.clientapp.generated.resources.client_map_search_placeholder
import top.clientapp.generated.resources.client_venue_closed
import top.clientapp.generated.resources.client_venue_status_open
import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.roundToInt
import kotlin.math.sin
import kotlin.math.sqrt
import kotlin.time.Duration.Companion.milliseconds

@Composable
fun ClientMapScreen(
    state: ClientMapViewState,
    onEvent: (ClientMapEvent) -> Unit,
    onVenueDetailsRequested: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(MineShaft50)
    ) {
        ClientMapLibreContent(
            cameraTarget = state.cameraTarget,
            markers = state.markers,
            selectedMarkerId = state.selectedVenueId,
            onMarkerClick = { onEvent(ClientMapEvent.OnMarkerClicked(it)) },
            onMapClick = { onEvent(ClientMapEvent.OnMapClicked) },
            onMapLoadFailed = { onEvent(ClientMapEvent.OnMapLoadFailed(it)) },
            onMapLoadFinished = { onEvent(ClientMapEvent.OnMapLoadFinished) },
            modifier = Modifier.fillMaxSize()
        )

        AppSearchField(
            value = state.searchQuery,
            onValueChange = { onEvent(ClientMapEvent.OnSearchChanged(it)) },
            placeholder = stringResource(Res.string.client_map_search_placeholder),
            modifier = Modifier
                .align(Alignment.TopCenter)
                .padding(start = 16.dp, top = 16.dp, end = 16.dp)
                .fillMaxWidth()
                .shadow(10.dp, RoundedCornerShape(8.dp))
        )

        if (state.isLoading) {
            MapLoadingIndicator(
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(top = 76.dp, end = 16.dp)
            )
        }

        state.mapErrorMessage?.let { error ->
            MapErrorPill(
                message = error,
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .padding(start = 16.dp, top = 76.dp, end = 16.dp)
            )
        }

        val selectedVenue = state.selectedVenue
        if (selectedVenue != null && !state.isListExpanded) {
            MapSelectedVenueCard(
                venue = selectedVenue,
                userLocation = state.userLocation,
                onClick = { onVenueDetailsRequested(selectedVenue.id) },
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .padding(horizontal = 16.dp, vertical = 14.dp)
            )
        } else {
            MapResultsSheet(
                state = state,
                onEvent = onEvent,
                onVenueDetailsRequested = onVenueDetailsRequested,
                modifier = Modifier.align(Alignment.BottomCenter)
            )
        }
    }
}

@Composable
private fun ClientMapLibreContent(
    cameraTarget: ClientMapCameraTarget,
    markers: List<ClientMapMarker>,
    selectedMarkerId: String?,
    onMarkerClick: (String) -> Unit,
    onMapClick: () -> Unit,
    onMapLoadFailed: (String?) -> Unit,
    onMapLoadFinished: () -> Unit,
    modifier: Modifier = Modifier
) {
    val styleUrl = remember {
        "https://api.maptiler.com/maps/streets-v4/style.json?key=${ClientMapTilerConfiguration.API_KEY}"
    }
    val cameraState = rememberCameraState(
        firstPosition = CameraPosition(
            target = Position(
                longitude = cameraTarget.longitude,
                latitude = cameraTarget.latitude
            ),
            zoom = cameraTarget.zoom,
            padding = PaddingValues(bottom = 180.dp)
        )
    )

    LaunchedEffect(cameraTarget) {
        cameraState.animateTo(
            finalPosition = cameraState.position.copy(
                target = Position(
                    longitude = cameraTarget.longitude,
                    latitude = cameraTarget.latitude
                ),
                zoom = cameraTarget.zoom,
                padding = PaddingValues(bottom = 180.dp)
            ),
            duration = 350.milliseconds
        )
    }

    Box(modifier = modifier) {
        MaplibreMap(
            modifier = Modifier.fillMaxSize(),
            baseStyle = BaseStyle.Uri(styleUrl),
            cameraState = cameraState,
            onMapClick = { _, _ ->
                onMapClick()
                ClickResult.Pass
            },
            onMapLoadFailed = onMapLoadFailed,
            onMapLoadFinished = onMapLoadFinished
        ) {
            ClientMarkerLayers(
                markers = markers,
                selectedMarkerId = selectedMarkerId,
                onMarkerClick = onMarkerClick
            )
        }

        MapMarkerIconOverlay(
            cameraState = cameraState,
            markers = markers,
            selectedMarkerId = selectedMarkerId,
            onMarkerClick = onMarkerClick,
            modifier = Modifier.fillMaxSize()
        )
    }
}

@Composable
private fun MapMarkerIconOverlay(
    cameraState: CameraState,
    markers: List<ClientMapMarker>,
    selectedMarkerId: String?,
    onMarkerClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val projection = cameraState.projection ?: return
    cameraState.position
    cameraState.isCameraMoving

    BoxWithConstraints(modifier = modifier) {
        markers.forEach { marker ->
            val isSelected = marker.venueId == selectedMarkerId
            val markerSize = if (isSelected) SelectedMarkerIconContainerSize else MarkerIconContainerSize
            val iconSize = if (isSelected) SelectedMarkerIconSize else MarkerIconSize
            val screenOffset = projection.screenLocationFromPosition(
                Position(
                    longitude = marker.longitude,
                    latitude = marker.latitude
                )
            )

            if (
                screenOffset.x < -markerSize ||
                screenOffset.y < -markerSize ||
                screenOffset.x > maxWidth + markerSize ||
                screenOffset.y > maxHeight + markerSize
            ) {
                return@forEach
            }

            Box(
                modifier = Modifier
                    .offset(
                        x = screenOffset.x - markerSize / 2,
                        y = screenOffset.y - markerSize / 2
                    )
                    .size(markerSize)
                    .clip(CircleShape)
                    .clickable { onMarkerClick(marker.venueId) },
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = marker.group.icon(),
                    contentDescription = marker.title,
                    tint = White,
                    modifier = Modifier.size(iconSize)
                )
            }
        }
    }
}

@Composable
@MaplibreComposable
private fun ClientMarkerLayers(
    markers: List<ClientMapMarker>,
    selectedMarkerId: String?,
    onMarkerClick: (String) -> Unit
) {
    val regularMarkers = remember(markers, selectedMarkerId) {
        markers.filterNot { it.venueId == selectedMarkerId }
    }
    val selectedMarkers = remember(markers, selectedMarkerId) {
        markers.filter { it.venueId == selectedMarkerId }
    }
    val regularSource = rememberGeoJsonSource(
        data = GeoJsonData.JsonString(regularMarkers.toFeatureCollectionJson()),
        options = GeoJsonOptions(synchronousUpdate = true)
    )
    val selectedSource = rememberGeoJsonSource(
        data = GeoJsonData.JsonString(selectedMarkers.toFeatureCollectionJson()),
        options = GeoJsonOptions(synchronousUpdate = true)
    )
    val markerClick = { features: List<org.maplibre.spatialk.geojson.Feature<org.maplibre.spatialk.geojson.Geometry, kotlinx.serialization.json.JsonObject?>> ->
        val venueId = features.firstOrNull()
            ?.properties
            ?.get("venueId")
            ?.jsonPrimitive
            ?.contentOrNull

        if (venueId == null) {
            ClickResult.Pass
        } else {
            onMarkerClick(venueId)
            ClickResult.Consume
        }
    }

    CircleLayer(
        id = "client-map-markers",
        source = regularSource,
        color = const(MapMarkerColor),
        radius = const(12.dp),
        strokeColor = const(White),
        strokeWidth = const(2.dp),
        onClick = markerClick
    )

    CircleLayer(
        id = "client-map-selected-marker-halo",
        source = selectedSource,
        color = const(MapMarkerHaloColor),
        radius = const(22.dp),
        strokeColor = const(Color.Transparent),
        strokeWidth = const(0.dp)
    )

    CircleLayer(
        id = "client-map-selected-marker",
        source = selectedSource,
        color = const(MapMarkerSelectedColor),
        radius = const(14.dp),
        strokeColor = const(White),
        strokeWidth = const(3.dp),
        onClick = markerClick
    )
}

@Composable
private fun MapResultsSheet(
    state: ClientMapViewState,
    onEvent: (ClientMapEvent) -> Unit,
    onVenueDetailsRequested: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val sheetHeight by animateDpAsState(
        targetValue = if (state.isListExpanded) 330.dp else 74.dp
    )

    Column(
        modifier = modifier
            .fillMaxWidth()
            .height(sheetHeight)
            .shadow(14.dp, RoundedCornerShape(topStart = 18.dp, topEnd = 18.dp))
            .clip(RoundedCornerShape(topStart = 18.dp, topEnd = 18.dp))
            .background(White)
            .border(
                width = 1.dp,
                color = MineShaft100,
                shape = RoundedCornerShape(topStart = 18.dp, topEnd = 18.dp)
            )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clickable { onEvent(ClientMapEvent.OnToggleListClicked) }
                .padding(horizontal = 20.dp, vertical = 10.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier = Modifier
                    .size(width = 44.dp, height = 4.dp)
                    .clip(CircleShape)
                    .background(MineShaft200)
            )

            Spacer(modifier = Modifier.height(12.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = if (state.venues.isEmpty()) {
                        stringResource(Res.string.client_map_empty_title)
                    } else {
                        stringResource(Res.string.client_map_results_count, state.venues.size.toString())
                    },
                    style = BodyMedium.copy(
                        color = MineShaft950,
                        fontSize = 18.sp,
                        lineHeight = 24.sp,
                        fontWeight = FontWeight.Medium
                    ),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f)
                )

                Icon(
                    painter = if (state.isListExpanded) AppIcons.ArrowDown else AppIcons.ArrowUp,
                    contentDescription = null,
                    tint = MineShaft500,
                    modifier = Modifier.size(22.dp)
                )
            }
        }

        AnimatedVisibility(visible = state.isListExpanded) {
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(start = 16.dp, end = 16.dp, bottom = 18.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                items(state.venues, key = VenueResponse::id) { venue ->
                    MapVenueListItem(
                        venue = venue,
                        userLocation = state.userLocation,
                        onClick = { onVenueDetailsRequested(venue.id) }
                    )
                }
            }
        }
    }
}

@Composable
private fun MapSelectedVenueCard(
    venue: VenueResponse,
    userLocation: ClientGeoLocation?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = 112.dp)
            .shadow(12.dp, RoundedCornerShape(8.dp))
            .clip(RoundedCornerShape(8.dp))
            .background(White)
            .border(1.dp, MineShaft200, RoundedCornerShape(8.dp))
            .clickable(onClick = onClick)
            .padding(10.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        VenueMapImage(
            venue = venue,
            modifier = Modifier.size(92.dp)
        )

        VenueMapInfo(
            venue = venue,
            userLocation = userLocation,
            modifier = Modifier.weight(1f)
        )
    }
}

@Composable
private fun MapVenueListItem(
    venue: VenueResponse,
    userLocation: ClientGeoLocation?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(White)
            .border(1.dp, MineShaft200, RoundedCornerShape(8.dp))
            .clickable(onClick = onClick)
            .padding(10.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        VenueMapImage(
            venue = venue,
            modifier = Modifier.size(72.dp)
        )

        VenueMapInfo(
            venue = venue,
            userLocation = userLocation,
            modifier = Modifier.weight(1f)
        )
    }
}

@Composable
private fun VenueMapImage(
    venue: VenueResponse,
    modifier: Modifier = Modifier
) {
    val imageUrl = venue.gallery.firstOrNull { !it.publicUrl.isNullOrBlank() }?.publicUrl
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(MineShaft50),
        contentAlignment = Alignment.Center
    ) {
        if (imageUrl.isNullOrBlank()) {
            Icon(
                painter = AppIcons.VenueImagePlaceholder,
                contentDescription = null,
                tint = Color.Unspecified,
                modifier = Modifier.size(32.dp)
            )
        } else {
            AppNetworkImage(
                model = imageUrl,
                contentDescription = venue.name,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize()
            )
        }
    }
}

@Composable
private fun VenueMapInfo(
    venue: VenueResponse,
    userLocation: ClientGeoLocation?,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.Top
        ) {
            Text(
                text = venue.name,
                style = BodyMedium.copy(
                    color = MineShaft900,
                    fontSize = 16.sp,
                    lineHeight = 20.sp,
                    fontWeight = FontWeight.Medium
                ),
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f)
            )

            Spacer(modifier = Modifier.width(8.dp))

            Row(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    painter = AppIcons.RatingStarFilled,
                    contentDescription = null,
                    tint = Color.Unspecified,
                    modifier = Modifier.size(14.dp)
                )
                Text(
                    text = venue.ratingSummaryText(),
                    style = BodyMedium.copy(
                        color = MineShaft950,
                        fontSize = 12.sp,
                        lineHeight = 16.sp
                    ),
                    maxLines = 1
                )
            }
        }

        VenueOpenStatusLine(venue = venue)

        Row(
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalAlignment = Alignment.Top
        ) {
            Icon(
                painter = AppIcons.VenueLocation,
                contentDescription = null,
                tint = MineShaft500,
                modifier = Modifier.size(15.dp)
            )

            Text(
                text = venue.location.address,
                style = BodyMedium.copy(
                    color = MineShaft600,
                    fontSize = 13.sp,
                    lineHeight = 18.sp
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f)
            )
        }

        venue.distanceLabel(userLocation)?.let { distance ->
            Text(
                text = distance,
                style = BodyMedium.copy(
                    color = MineShaft400,
                    fontSize = 12.sp,
                    lineHeight = 16.sp
                ),
                maxLines = 1
            )
        }
    }
}

@Composable
private fun VenueOpenStatusLine(venue: VenueResponse) {
    val isOpen = remember(venue.schedule) { venue.isOpenNow() }
    val color = if (isOpen) ActiveStatusColor else ClosedStatusColor

    Row(
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(7.dp)
                .clip(CircleShape)
                .background(color)
        )
        Text(
            text = stringResource(
                if (isOpen) {
                    Res.string.client_venue_status_open
                } else {
                    Res.string.client_venue_closed
                }
            ),
            style = BodyMedium.copy(
                color = color,
                fontSize = 13.sp,
                lineHeight = 18.sp,
                fontWeight = FontWeight.Medium
            ),
            maxLines = 1
        )
    }
}

@Composable
private fun MapLoadingIndicator(modifier: Modifier = Modifier) {
    Row(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(White)
            .border(1.dp, MineShaft200, RoundedCornerShape(8.dp))
            .padding(horizontal = 12.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        CircularProgressIndicator(
            color = Primary600,
            strokeWidth = 2.dp,
            modifier = Modifier.size(18.dp)
        )
        Text(
            text = stringResource(Res.string.client_map_loading),
            style = BodyMedium.copy(
                color = MineShaft950,
                fontSize = 13.sp,
                lineHeight = 18.sp
            ),
            maxLines = 1
        )
    }
}

@Composable
private fun MapErrorPill(
    message: String,
    modifier: Modifier = Modifier
) {
    Text(
        text = message,
        style = BodyMedium.copy(
            color = ClosedStatusColor,
            fontSize = 13.sp,
            lineHeight = 18.sp,
            fontWeight = FontWeight.Medium
        ),
        maxLines = 2,
        overflow = TextOverflow.Ellipsis,
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(White)
            .border(1.dp, ClosedStatusColor.copy(alpha = 0.28f), RoundedCornerShape(8.dp))
            .padding(horizontal = 12.dp, vertical = 8.dp)
    )
}

private fun List<ClientMapMarker>.toFeatureCollectionJson(): String {
    return buildJsonObject {
        put("type", JsonPrimitive("FeatureCollection"))
        put(
            "features",
            buildJsonArray {
                this@toFeatureCollectionJson.forEach { marker ->
                    add(marker.toFeatureJson())
                }
            }
        )
    }.toString()
}

private fun ClientMapMarker.toFeatureJson() = buildJsonObject {
    put("type", JsonPrimitive("Feature"))
    put(
        "properties",
        buildJsonObject {
            put("venueId", JsonPrimitive(venueId))
            put("title", JsonPrimitive(title))
        }
    )
    put(
        "geometry",
        buildJsonObject {
            put("type", JsonPrimitive("Point"))
            put(
                "coordinates",
                buildJsonArray {
                    add(JsonPrimitive(longitude))
                    add(JsonPrimitive(latitude))
                }
            )
        }
    )
}

private fun VenueResponse.isOpenNow(): Boolean {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    val day = now.date.dayOfWeek.toVenueDayOfWeek()
    val workDay = schedule.workDays.firstOrNull { it.day == day } ?: return false
    if (!workDay.isOpen) return false

    val openTime = workDay.openTime
    val closeTime = workDay.closeTime
    if (openTime == null || closeTime == null) return true

    val nowMinutes = now.time.minutesOfDay()
    val openMinutes = openTime.minutesOfDay()
    val closeMinutes = closeTime.minutesOfDay()
    return if (closeMinutes >= openMinutes) {
        nowMinutes in openMinutes..closeMinutes
    } else {
        nowMinutes >= openMinutes || nowMinutes <= closeMinutes
    }
}

@Suppress("REDUNDANT_ELSE_IN_WHEN")
private fun kotlinx.datetime.DayOfWeek.toVenueDayOfWeek(): VenueDayOfWeek = when (this) {
    kotlinx.datetime.DayOfWeek.MONDAY -> VenueDayOfWeek.MONDAY
    kotlinx.datetime.DayOfWeek.TUESDAY -> VenueDayOfWeek.TUESDAY
    kotlinx.datetime.DayOfWeek.WEDNESDAY -> VenueDayOfWeek.WEDNESDAY
    kotlinx.datetime.DayOfWeek.THURSDAY -> VenueDayOfWeek.THURSDAY
    kotlinx.datetime.DayOfWeek.FRIDAY -> VenueDayOfWeek.FRIDAY
    kotlinx.datetime.DayOfWeek.SATURDAY -> VenueDayOfWeek.SATURDAY
    kotlinx.datetime.DayOfWeek.SUNDAY -> VenueDayOfWeek.SUNDAY
    else -> VenueDayOfWeek.MONDAY
}

private fun LocalTime.minutesOfDay(): Int = hour * 60 + minute

private fun VenueResponse.distanceLabel(userLocation: ClientGeoLocation?): String? {
    val distance = distanceTo(userLocation) ?: return null
    return when {
        distance < 1.0 -> "${(distance * 1000).roundToInt().coerceAtLeast(50)} м"
        else -> "${distance.compactDistance()} км"
    }
}

private fun VenueResponse.distanceTo(userLocation: ClientGeoLocation?): Double? {
    val coordinates = location.coordinates ?: return null
    userLocation ?: return null
    return haversineDistanceKm(
        latitude1 = userLocation.latitude,
        longitude1 = userLocation.longitude,
        latitude2 = coordinates.latitude,
        longitude2 = coordinates.longitude
    )
}

private fun haversineDistanceKm(
    latitude1: Double,
    longitude1: Double,
    latitude2: Double,
    longitude2: Double
): Double {
    val latitudeDelta = (latitude2 - latitude1).toRadians()
    val longitudeDelta = (longitude2 - longitude1).toRadians()
    val startLatitude = latitude1.toRadians()
    val endLatitude = latitude2.toRadians()
    val a = sin(latitudeDelta / 2).pow(2) +
        cos(startLatitude) * cos(endLatitude) * sin(longitudeDelta / 2).pow(2)

    return EarthRadiusKm * 2 * atan2(sqrt(a), sqrt(1 - a))
}

private fun Double.toRadians(): Double = this * PI / 180.0

private fun Double.compactDistance(): String {
    val rounded = (this * 10).roundToInt()
    val whole = rounded / 10
    val decimal = rounded % 10
    return if (decimal == 0) whole.toString() else "$whole.$decimal"
}

private val MapMarkerColor = Color(0xFFA7784F)
private val MapMarkerSelectedColor = Color(0xFF007A55)
private val MapMarkerHaloColor = Color(0x33007A55)
private val ActiveStatusColor = Color(0xFF007A55)
private val ClosedStatusColor = Color(0xFF956744)
private val MarkerIconContainerSize = 28.dp
private val SelectedMarkerIconContainerSize = 34.dp
private val MarkerIconSize = 16.dp
private val SelectedMarkerIconSize = 19.dp
private const val EarthRadiusKm = 6371.0
